import { LocationData, LocationResult, LocationSource, GPSPosition, LocationError, LocationErrorType } from '../types/location.types';
import { WeatherCache } from './weatherCache';
import { retry } from '../utils/weatherHelpers';

export class GeolocationService {
  private static readonly IP_API_URL = 'https://ipapi.co/json/';
  private static readonly GEOCODE_API_URL = 'https://nominatim.openstreetmap.org/reverse';
  private static readonly GEOCODE_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
  private static readonly GPS_TIMEOUT = 10000; // 10 seconds
  private static readonly DEFAULT_LOCATION = {
    city: 'Madison',
    region: 'WI',
    country: 'US',
    countryName: 'United States',
    latitude: 43.0731,
    longitude: -89.4012,
    timezone: 'America/Chicago',
  };

  /**
   * Get location with GPS priority, then IP fallback
   */
  static async getLocation(): Promise<LocationResult> {
    // Try GPS first
    try {
      const gpsResult = await this.getGPSLocation();
      return gpsResult;
    } catch (gpsError) {
      console.warn('GPS location failed, trying IP fallback:', gpsError);

      // Fallback to IP location
      try {
        const ipResult = await this.getIPLocation();
        return ipResult;
      } catch (ipError) {
        console.warn('IP location failed, using default location:', ipError);

        // Final fallback to default location
        return {
          location: this.DEFAULT_LOCATION,
          source: 'default',
          timestamp: Date.now(),
          error: 'All location services failed',
        };
      }
    }
  }

  /**
   * Get GPS location from browser Geolocation API
   */
  static async getGPSLocation(): Promise<LocationResult> {
    if (!navigator.geolocation) {
      throw this.createLocationError(
        LocationErrorType.POSITION_UNAVAILABLE,
        'GPS is not supported by your browser'
      );
    }

    return new Promise((resolve, reject) => {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: this.GPS_TIMEOUT,
        maximumAge: 5 * 60 * 1000, // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const gpsPosition: GPSPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };

          // Reverse geocode to get city/region info
          try {
            const locationData = await this.reverseGeocode(
              gpsPosition.latitude,
              gpsPosition.longitude
            );

            const result: LocationResult = {
              location: locationData,
              source: 'gps',
              accuracy: gpsPosition.accuracy,
              timestamp: Date.now(),
            };

            // Cache the GPS location
            WeatherCache.setLocation(locationData, 'gps');

            resolve(result);
          } catch (geocodeError) {
            // If reverse geocoding fails, still use GPS coordinates with minimal info
            const result: LocationResult = {
              location: {
                city: 'Unknown',
                region: 'Unknown',
                country: 'Unknown',
                countryName: 'Unknown',
                latitude: gpsPosition.latitude,
                longitude: gpsPosition.longitude,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
              source: 'gps',
              accuracy: gpsPosition.accuracy,
              timestamp: Date.now(),
              error: 'Reverse geocoding failed',
            };

            resolve(result);
          }
        },
        (error) => {
          reject(this.handleGPSError(error));
        },
        options
      );
    });
  }

  /**
   * Get location from IP address
   */
  static async getIPLocation(): Promise<LocationResult> {
    try {
      const locationData = await retry(
        async () => {
          const response = await fetch(this.IP_API_URL);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();

          if (data.error) {
            throw new Error(data.reason || 'Geolocation API error');
          }

          return {
            ip: data.ip,
            city: data.city || 'Unknown',
            region: data.region || 'Unknown',
            country: data.country_code || 'Unknown',
            countryName: data.country_name || 'Unknown',
            latitude: parseFloat(data.latitude),
            longitude: parseFloat(data.longitude),
            timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            isp: data.org,
          };
        },
        { maxAttempts: 2, delay: 1000, backoff: false }
      );

      // Cache the IP location
      WeatherCache.setLocation(locationData, 'ip');

      return {
        location: locationData,
        source: 'ip',
        timestamp: Date.now(),
      };
    } catch (error) {
      throw this.createLocationError(
        LocationErrorType.NETWORK_ERROR,
        'Failed to get location from IP address'
      );
    }
  }

  /**
   * Reverse geocode GPS coordinates to get location details
   */
  private static async reverseGeocode(latitude: number, longitude: number): Promise<LocationData> {
    try {
      const params = new URLSearchParams({
        lat: latitude.toString(),
        lon: longitude.toString(),
        format: 'json',
        'accept-language': 'en',
      });

      const response = await fetch(`${this.GEOCODE_API_URL}?${params}`, {
        headers: {
          'User-Agent': 'WeatherApp/1.0', // Required by Nominatim
        },
      });

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }

      const data = await response.json();

      return {
        city: data.address?.city || data.address?.town || data.address?.village || 'Unknown',
        region: data.address?.state || data.address?.region || 'Unknown',
        country: data.address?.country_code?.toUpperCase() || 'Unknown',
        countryName: data.address?.country || 'Unknown',
        latitude,
        longitude,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      throw error;
    }
  }

  /**
   * Check if GPS permission is granted
   */
  static async checkGPSPermission(): Promise<PermissionState> {
    if (!navigator.permissions) {
      return 'prompt';
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      return result.state;
    } catch (error) {
      console.warn('Permission API not supported:', error);
      return 'prompt';
    }
  }

  /**
   * Get cached location if available
   */
  static getCachedLocation(): LocationResult | null {
    const cached = WeatherCache.getLocation();
    if (cached) {
      return {
        location: cached.data,
        source: cached.source as LocationSource,
        timestamp: Date.now() - cached.age,
      };
    }
    return null;
  }

  /**
   * Handle GPS errors
   */
  private static handleGPSError(error: GeolocationPositionError): LocationError {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return this.createLocationError(
          LocationErrorType.PERMISSION_DENIED,
          'Location permission denied'
        );
      case error.POSITION_UNAVAILABLE:
        return this.createLocationError(
          LocationErrorType.POSITION_UNAVAILABLE,
          'Location information unavailable'
        );
      case error.TIMEOUT:
        return this.createLocationError(
          LocationErrorType.TIMEOUT,
          'Location request timed out'
        );
      default:
        return this.createLocationError(
          LocationErrorType.UNKNOWN,
          'Unknown location error'
        );
    }
  }

  /**
   * Create a structured location error
   */
  private static createLocationError(type: LocationErrorType, message: string): LocationError {
    const errorMessages: Record<LocationErrorType, { userMessage: string; suggestion: string }> = {
      [LocationErrorType.PERMISSION_DENIED]: {
        userMessage: 'Location access was denied',
        suggestion: 'Enable location in your browser settings or we\'ll use your IP address',
      },
      [LocationErrorType.POSITION_UNAVAILABLE]: {
        userMessage: 'Your location is currently unavailable',
        suggestion: 'Check your device settings or we\'ll use your IP address',
      },
      [LocationErrorType.TIMEOUT]: {
        userMessage: 'Location request took too long',
        suggestion: 'Try again or we\'ll use your IP address',
      },
      [LocationErrorType.NETWORK_ERROR]: {
        userMessage: 'Network error while getting location',
        suggestion: 'Check your internet connection and try again',
      },
      [LocationErrorType.UNKNOWN]: {
        userMessage: 'Unable to get your location',
        suggestion: 'We\'ll use default location instead',
      },
    };

    const details = errorMessages[type];

    return {
      type,
      message,
      userMessage: details.userMessage,
      suggestion: details.suggestion,
    };
  }

  /**
   * Search for a city by name
   */
  static async searchCity(cityName: string): Promise<LocationResult[]> {
    if (!cityName || cityName.trim().length < 2) {
      return [];
    }

    try {
      const params = new URLSearchParams({
        q: cityName.trim(),
        format: 'json',
        limit: '5',
        addressdetails: '1', // Get detailed address components
        'accept-language': 'en',
      });

      const response = await fetch(`${this.GEOCODE_SEARCH_URL}?${params}`, {
        headers: {
          'User-Agent': 'WeatherApp/1.0',
        },
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();

      // Map and deduplicate results
      const results = data.map((item: any) => {
        // Use the display_name from Nominatim which has full location context
        const displayName = item.display_name || item.name || 'Unknown';

        // Extract city name (fallback for internal use)
        const city = item.address?.city || item.address?.town || item.address?.village || item.name || 'Unknown';

        // Extract state/region
        const region = item.address?.state || item.address?.region || '';

        // Extract country
        const country = item.address?.country_code?.toUpperCase() || '';
        const countryName = item.address?.country || '';

        const location: LocationData = {
          city,
          region,
          country,
          countryName,
          latitude: parseFloat(item.lat),
          longitude: parseFloat(item.lon),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          displayName, // Use Nominatim's pre-formatted display_name
        };

        return {
          location,
          source: 'ip' as LocationSource,
          timestamp: Date.now(),
        };
      });

      // Deduplicate based on display_name and coordinates
      const seen = new Set<string>();
      const deduplicated = results.filter((result) => {
        // Create unique key from display_name and rounded coordinates
        const lat = result.location.latitude.toFixed(2);
        const lon = result.location.longitude.toFixed(2);
        const key = `${result.location.displayName}|${lat}|${lon}`;

        if (seen.has(key)) {
          return false; // Skip duplicate
        }

        seen.add(key);
        return true;
      });

      return deduplicated;
    } catch (error) {
      console.warn('City search failed:', error);
      return [];
    }
  }

  /**
   * Format location for display (without IP)
   */
  static formatLocation(location: LocationData, source: LocationSource): string {
    const parts = [location.city, location.region, location.countryName].filter(
      (part) => part && part !== 'Unknown'
    );

    const locationStr = parts.join(', ') || 'Unknown Location';

    return locationStr;
  }
}
